import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import { useHistory } from 'react-router-dom';
import { useFloatingAIChat } from '../store';

/**
 * Where the user was when they handed off to the scheduling agent.
 *
 * Distinct from `SchedulingEntryPoint` in the CRM store, which attributes the scheduling FORM.
 * This one attributes the AGENT, and it is the property that answers "where do AI schedulings come
 * from" — before it existed, every entry point emitted the same event with no way to tell them
 * apart, and two of them emitted nothing at all.
 */
export type SchedulingAgentEntryPoint =
  | 'fab'
  | 'nudge'
  | 'beacon'
  | 'beacon-unassociated'
  | 'upcoming-tickets'
  | 'order-timeline'
  | 'order-summary'
  | 'orders-calendar'
  | 'orders-calendar-next-available'
  | 'orders-calendar-uninstallations'
  | 'orders-ai-page'
  | 'orders-header'
  | 'onboarding';

/**
 * The single entry point for handing the user over to the scheduling agent.
 *
 * Every surface that offers scheduling goes through here, so the prompts have one owner instead of
 * living as string literals in each caller — the ticket prompt alone was copy-pasted in three
 * components.
 *
 * It emits the canonical `AI - Floating Chat - Opened` with the `entryPoint`. Callers keep their
 * own domain events (a beacon click and a calendar click mean different things); this one exists so
 * the agent funnel has a single, attributable source.
 */
export const useSchedulingAgent = () => {
  const open = useFloatingAIChat((s) => s.open);
  // The prompt lands in the chat as the user's OWN opening message, so it follows the user's locale
  // — hardcoded Portuguese showed an en-US user a message they never wrote.
  const { t } = useTranslation('ai');
  const { ordersAiPage } = useFeatureFlags();
  const history = useHistory();

  /**
   * Experimento `orders-ai-page`: com a flag ligada, TODA entrada do agente de agendamento passa a
   * levar para a página em vez de abrir o painel flutuante — FAB, nudge, beacon, calendário, linha
   * do tempo, resumo do pedido e onboarding. É exatamente a troca que o experimento mede: mesmas
   * portas, superfície diferente.
   *
   * Centralizar aqui é o que torna isso uma mudança e não sete: cada chamador continua dizendo
   * "leve o usuário ao agente com este prompt" e não precisa saber onde o agente mora hoje.
   */
  const handoff = (prompt: string | undefined, entryPoint: SchedulingAgentEntryPoint, extra?: Record<string, unknown>) => {
    Analytics.track('AI - Floating Chat - Opened', {
      ...extra,
      component: 'scheduling-agent',
      journeyId: 'installation',
      entryPoint,
      prompt,
      surface: ordersAiPage ? 'orders-ai-page' : 'floating',
    });

    if (ordersAiPage) {
      // A página lê `prompt` na montagem e manda como primeira mensagem, como no design.
      // Sem prompt, ela abre na apresentação do agente com os quatro cartões.
      history.push(prompt ? `${Location.ORDERS_AI}?prompt=${encodeURIComponent(prompt)}` : Location.ORDERS_AI);
      return;
    }

    open(prompt);
  };

  return {
    /** The fleet has one specific pending ticket — the agent starts already scoped to it. */
    openForTicket: (ticketId: string, entryPoint: SchedulingAgentEntryPoint) =>
      handoff(t('floating_chat_prompts.installation_ticket', { ticketId }), entryPoint, { ticketId, scoped: true }),
    /** Several (or unspecified) pending installations — the agent starts from the fleet. */
    openForPendingInstalls: (entryPoint: SchedulingAgentEntryPoint) => handoff(t('floating_chat_prompts.installation'), entryPoint, { scoped: false }),
    /** Any other scheduling conversation (a date, uninstallations, an unassociated-device scenario). */
    openWithPrompt: (prompt: string, entryPoint: SchedulingAgentEntryPoint) => handoff(prompt, entryPoint, { scoped: false }),
    /**
     * Leva ao agente SEM assunto: a pessoa escolhe o que fazer.
     *
     * Existe porque nem toda porta sabe o que o usuário quer. As outras aparecem num contexto que
     * já diz o assunto (um ticket pendente, uma data no calendário); o botão do topo de Pedidos é
     * só "quero falar com o agente". Mandar um prompt ali responderia por ele e derrubaria o valor
     * da tela de apresentação, que é justamente o que o experimento quer testar.
     */
    openHome: (entryPoint: SchedulingAgentEntryPoint) => handoff(undefined, entryPoint, { scoped: false }),
  };
};

export default useSchedulingAgent;
